import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEvent, useState } from 'react';
import {
  DashboardView,
  dashboardViewQuery,
  saveDashboardView,
} from '../../api/command-dashboard-api';
import { getColor } from '../../shared/color-utils';
import { handleErrors } from '../../utils/errors';
import { Messages } from '../../utils/message-constants';
import { useStore } from '../state/zustand-store';
import { useCommandDashboardTilesStore } from './command-dashboard-tiles-store';
import SaveDashboardViewDialog from './save-dashboard-view-dialog';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Add from '@mui/icons-material/Add';
import CollectionsBookmarkOutlined from '@mui/icons-material/CollectionsBookmarkOutlined';
import Delete from '@mui/icons-material/Delete';
import Save from '@mui/icons-material/Save';

export const DEFAULT_DASHBOARD_VIEW = 'Default';

// Including the synthetic "Default" entry.
export const MAX_SAVED_DASHBOARD_VIEWS = 6;

interface SavedViewsIconButtonProps {
  badgeColor: string;
  disabled?: boolean;
  menuOpen?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

/**
 * The trigger itself, shared by the loading and loaded states so the toolbar
 * keeps the same shape either way.
 */
function SavedViewsIconButton({
  badgeColor,
  disabled = false,
  menuOpen = false,
  onClick,
}: SavedViewsIconButtonProps) {
  const theme = useTheme();
  const shade = theme.palette.mode === 'light' ? 300 : 500;
  const label = disabled ? 'Loading dashboard views' : 'Save Dashboard View';

  return (
    <Tooltip arrow title={label}>
      {/* A disabled button emits no pointer events, so the tooltip needs a
          wrapper element of its own to listen on. */}
      <span>
        <IconButton
          id="dashboard-saved-views-button"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          aria-controls={menuOpen ? 'dashboard-saved-views-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? 'true' : undefined}
        >
          <Badge
            variant="dot"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            sx={{
              '& .MuiBadge-dot': {
                backgroundColor: getColor(badgeColor)[shade],
              },
            }}
          >
            <CollectionsBookmarkOutlined />
          </Badge>
        </IconButton>
      </span>
    </Tooltip>
  );
}

interface DashboardViewMenuButtonProps {
  /**
   * Undefined until the dashboard view query resolves. The button is rendered
   * disabled rather than omitted during that window, so it holds its place in
   * the toolbar instead of popping in and shifting everything beside it.
   */
  dashboardView?: DashboardView;
}

/** Bookmark-style icon button + menu for saving, switching between, and deleting dashboard views. */
export default function DashboardViewMenuButton({
  dashboardView,
}: DashboardViewMenuButtonProps) {
  if (!dashboardView) {
    return <SavedViewsIconButton badgeColor="grey" disabled />;
  }
  return <LoadedDashboardViewMenuButton dashboardView={dashboardView} />;
}

function LoadedDashboardViewMenuButton({
  dashboardView,
}: {
  dashboardView: DashboardView;
}) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const theme = useTheme();
  const tileRows = useCommandDashboardTilesStore((state) => state.tileRows);
  const hydrate = useCommandDashboardTilesStore((state) => state.hydrate);
  const [hoveringView, setHoveringView] = useState<string | null>(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const shade = theme.palette.mode === 'light' ? 300 : 500;

  const defaultViewTileRows = dashboardView.tileRows;

  const activeView = dashboardView.activeView || DEFAULT_DASHBOARD_VIEW;
  const savedViews = [
    ...[...dashboardView.savedViews].sort(
      (a, b) => b.createdDate - a.createdDate
    ),
    {
      name: DEFAULT_DASHBOARD_VIEW,
      createdDate: Date.now(),
      color: 'grey',
      tileRows: defaultViewTileRows,
    },
  ];

  const currentActiveView = savedViews.find((view) => view.name === activeView);
  const reachedMaxSavedViews = savedViews.length >= MAX_SAVED_DASHBOARD_VIEWS;
  const isDefaultView = currentActiveView?.name === DEFAULT_DASHBOARD_VIEW;

  const { mutate, isPending, variables } = useMutation({
    mutationFn: (data: DashboardView) => saveDashboardView(data),
    onSuccess: (savedView) => {
      queryClient.setQueryData(dashboardViewQuery().queryKey, savedView);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'this dashboard view'
        ),
      });
    },
  });

  const selectView = (view: (typeof savedViews)[number]) => {
    hydrate(view.tileRows);
    mutate({ ...dashboardView, activeView: view.name });
    setAnchorEl(null);
  };

  const deleteView = (name: string) => {
    const wasActiveView = dashboardView.activeView === name;
    if (wasActiveView) {
      hydrate(defaultViewTileRows);
    }
    mutate({
      ...dashboardView,
      activeView: wasActiveView
        ? DEFAULT_DASHBOARD_VIEW
        : dashboardView.activeView,
      savedViews: dashboardView.savedViews.filter((view) => view.name !== name),
    });
  };

  const updateActiveView = () => {
    if (!currentActiveView || isDefaultView) {
      return;
    }
    mutate(
      {
        ...dashboardView,
        savedViews: dashboardView.savedViews.map((view) =>
          view.name === currentActiveView.name ? { ...view, tileRows } : view
        ),
      },
      {
        onSuccess: () => {
          setSnackbar({
            open: true,
            severity: 'success',
            message: Messages.Success.Updated('Dashboard view'),
          });
        },
      }
    );
  };

  return (
    <>
      <SavedViewsIconButton
        badgeColor={currentActiveView?.color || 'grey'}
        menuOpen={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      />
      <Menu
        id="dashboard-saved-views-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ width: 275 }}>
          <ListSubheader
            sx={{
              lineHeight: 2.5,
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'transparent',
            }}
          >
            Saved Dashboard Views
          </ListSubheader>
          {savedViews.map((view, index) => (
            <MenuItem
              selected={view.name === activeView}
              key={view.name}
              onClick={() => selectView(view)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                height: 42,
                pr: 0.5,
                '&.Mui-selected': {
                  backgroundColor: (theme) => theme.palette.primary.light,
                },
              }}
              autoFocus={index === 0}
              onMouseEnter={() => setHoveringView(view.name)}
              onMouseLeave={() => setHoveringView(null)}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ width: '73%' }}
              >
                <Box
                  sx={{
                    backgroundColor: getColor(view.color)[shade],
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {view.name}
                </Typography>
              </Stack>
              {hoveringView === view.name &&
              view.name !== DEFAULT_DASHBOARD_VIEW ? (
                <Stack direction="row" alignItems="center" gap={0.25}>
                  {view.name === activeView &&
                  isPending &&
                  variables?.activeView === view.name ? (
                    <CircularProgress size={16} />
                  ) : view.name === activeView ? (
                    <Tooltip title="Update Active View" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateActiveView();
                        }}
                      >
                        <Save fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Delete View" arrow placement="top">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteView(view.name);
                      }}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : (
                <Box width={100} />
              )}
            </MenuItem>
          ))}
          <Divider />
          <Tooltip
            title={
              reachedMaxSavedViews
                ? 'You have reached the maximum number of saved views. Delete a view to save a new one.'
                : ''
            }
            arrow
            placement="bottom"
          >
            <span>
              <MenuItem
                onClick={() => setOpenCreateDialog(true)}
                disabled={reachedMaxSavedViews}
              >
                <ListItemIcon>
                  <Add />
                </ListItemIcon>
                <ListItemText>Save New Dashboard View</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Box>
      </Menu>
      {openCreateDialog && (
        <SaveDashboardViewDialog
          open={openCreateDialog}
          handleClose={() => setOpenCreateDialog(false)}
          dashboardView={dashboardView}
        />
      )}
    </>
  );
}
